import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { getPcaLineOfBusiness } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/utility";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider";
import { DropdownItem } from "@app/shared/checkbox-dropdown";

export interface NetworkComparisonFiltersContextValues {
  displayedNetworkComparisonFilters: NetworkComparisonFiltersPayload;
  getNetworkComparisonFilterData: (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload) => void;
  getNetworkComparisonFiltersPayload: () => NetworkComparisonFiltersPayload;
  mappedEffectivenessAndEfficiencyValues: DropdownItem<EffectivenessEfficiencyValueEnum>[];
  mappedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[];
  onClearNetworkComparisonFiltersClick: () => void;
  originalDisplayedNetworkComparisonFilters: NetworkComparisonFiltersPayload;
  originalSelectedNetwork: BenchmarkingNetworkType;
  selectedNetwork: BenchmarkingNetworkType;
  selectedEffectivenessValues: DropdownItem<EffectivenessEfficiencyValueEnum>[];
  selectedEfficiencyValues: DropdownItem<EffectivenessEfficiencyValueEnum>[]
  selectedPcaLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum;
  selectedSpecialty: number;
  selectedVolumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[];
  setDisplayedNetworkComparisonFilters: (networkCommparisonFiltersPayload: NetworkComparisonFiltersPayload) => void;
  setOriginalDisplayedNetworkComparisonFilters: (networkCommparisonFiltersPayload: NetworkComparisonFiltersPayload) => void;
  setSelectedEffectivenessValues: (selectedEffectivenessValues: DropdownItem<EffectivenessEfficiencyValueEnum>[]) => void;
  setSelectedEfficiencyValues: (selectedEfficiencyValues: DropdownItem<EffectivenessEfficiencyValueEnum>[]) => void;
  setSelectedNetwork: (network: BenchmarkingNetworkType) => void;
  setSelectedPcaLineOfBusiness: (pcaLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum) => void;
  setSelectedSpecialty: (specialty: number) => void;
  setSelectedVolumeIndicators: (volumeIndicator: DropdownItem<ClaimsActivityVolumeLabelEnum>[]) => void;
}

const initialValues: NetworkComparisonFiltersContextValues = {
  displayedNetworkComparisonFilters: null,
  getNetworkComparisonFilterData: () => null,
  getNetworkComparisonFiltersPayload: () => null,
  mappedEffectivenessAndEfficiencyValues: [],
  mappedVolumeIndicators: [],
  onClearNetworkComparisonFiltersClick: () => null,
  originalDisplayedNetworkComparisonFilters: null,
  originalSelectedNetwork: null,
  selectedEffectivenessValues: [],
  selectedEfficiencyValues: [],
  selectedNetwork: null,
  selectedPcaLineOfBusiness: null,
  selectedSpecialty: null,
  selectedVolumeIndicators: [],
  setDisplayedNetworkComparisonFilters: () => null,
  setOriginalDisplayedNetworkComparisonFilters: () => null,
  setSelectedEffectivenessValues: () => null,
  setSelectedEfficiencyValues: () => null,
  setSelectedNetwork: () => null,
  setSelectedPcaLineOfBusiness: () => null,
  setSelectedSpecialty: () => null,
  setSelectedVolumeIndicators: () => null,
}

export const NetworkComparisonFiltersContext = createContext(initialValues);

const NetworkComparisonFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [displayedNetworkComparisonFilters, setDisplayedNetworkComparisonFilters] = useState<NetworkComparisonFiltersPayload>(initialValues.displayedNetworkComparisonFilters);
  const [mappedEffectivenessAndEfficiencyValues, setMappedEffectivenessAndEfficiencyValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>(initialValues.mappedEffectivenessAndEfficiencyValues);
  const [mappedVolumeIndicators, setMappedVolumeIndicators] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>(initialValues.mappedVolumeIndicators);
  const [originalDisplayedNetworkComparisonFilters, setOriginalDisplayedNetworkComparisonFilters] = useState<NetworkComparisonFiltersPayload>(initialValues.originalDisplayedNetworkComparisonFilters);
  const [originalSelectedNetwork, setOriginalSelectedNetwork] = useState<BenchmarkingNetworkType>(initialValues.originalSelectedNetwork);
  const [selectedEffectivenessValues, setSelectedEffectivenessValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>([]);
  const [selectedEfficiencyValues, setSelectedEfficiencyValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<BenchmarkingNetworkType>(initialValues.selectedNetwork);
  const [selectedPcaLineOfBusiness, setSelectedPcaLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(initialValues.selectedPcaLineOfBusiness);
  const [selectedSpecialty, setSelectedSpecialty] = useState<number>(initialValues.selectedSpecialty);
  const [selectedVolumeIndicators, setSelectedVolumeIndicators] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>(initialValues.selectedVolumeIndicators);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  const volumeIndicators: DropdownItem<ClaimsActivityVolumeLabelEnum>[] = Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => {
    return { key: x, name: x }
  });

  const effectivenessAndEfficiencyValues: DropdownItem<EffectivenessEfficiencyValueEnum>[] = Object.values(EffectivenessEfficiencyValueEnum).map(x => {
    return { key: x, name: x }
  });

  useEffect(() => {
    setMappedVolumeIndicators(cloneDeep(volumeIndicators));
    setMappedEffectivenessAndEfficiencyValues(cloneDeep(effectivenessAndEfficiencyValues));
  }, []);

  useEffect(() => {
    if (!benchmarkingFiltersContext.displayedNetworkFilters.length || !benchmarkingFiltersContext.displayedPrimaryFilters) {
      // if the first section of filters is reset, set all the network comparison filters to an empty null state
      resetNetworkComparisonFilters();
    }
  }, [benchmarkingFiltersContext.displayedNetworkFilters, benchmarkingFiltersContext.displayedPrimaryFilters]);

  const resetNetworkComparisonFilters = () => {
    setDisplayedNetworkComparisonFilters(null);
    setOriginalDisplayedNetworkComparisonFilters(null);
    setSelectedNetwork(null);
    setOriginalSelectedNetwork(null);
    setSelectedPcaLineOfBusiness(null);
    setSelectedSpecialty(null);
    setSelectedVolumeIndicators([]);
    setSelectedEffectivenessValues([]);
    setSelectedEfficiencyValues([]);
  }

  useEffect(() => {
    if (!!displayedNetworkComparisonFilters) {
      resetNetworkComparisonFilters();
    }
  }, [benchmarkingFiltersContext.selectedNetworks]);

  const getNetworkComparisonFilterData = (networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): void => {
    setDisplayedNetworkComparisonFilters(networkComparisonFiltersPayload);
    setOriginalDisplayedNetworkComparisonFilters(cloneDeep(networkComparisonFiltersPayload));
    setSelectedNetwork(networkComparisonFiltersPayload.primaryNetwork);
    setOriginalSelectedNetwork(networkComparisonFiltersPayload.primaryNetwork);
    setSelectedPcaLineOfBusiness(networkComparisonFiltersPayload.pcaLineOfBusiness);
    setSelectedSpecialty(networkComparisonFiltersPayload.specialtyGroupId);
    setSelectedVolumeIndicators(cloneDeep(volumeIndicators));
    setSelectedEffectivenessValues(cloneDeep(effectivenessAndEfficiencyValues));
    setSelectedEfficiencyValues(cloneDeep(effectivenessAndEfficiencyValues));
  }

  const getNetworkComparisonFiltersPayload = (): NetworkComparisonFiltersPayload => {
    const filteredHContracts: BenchmarkingNetworkType[] = benchmarkingFiltersContext.selectedNetworks.filter(x => x.contractId !== selectedNetwork.contractId);

    return {
      comparisonNetworks: filteredHContracts,
      countyIds: benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds,
      effectivenessRanks: selectedEffectivenessValues.map(x => x.key),
      efficiencyRanks: selectedEfficiencyValues.map(x => x.key),
      pcaLineOfBusiness: selectedPcaLineOfBusiness,
      primaryNetwork: selectedNetwork,
      specialtyGroupId: selectedSpecialty,
      volumeIndicators: selectedVolumeIndicators.map(x => x.key),
    };
  }

  const onClearNetworkComparisonFiltersClick = (): void => {
    setSelectedNetwork(originalSelectedNetwork);
    setSelectedSpecialty(0);
    setSelectedPcaLineOfBusiness(getPcaLineOfBusiness(benchmarkingFiltersContext.selectedLineOfBusiness));
    setSelectedVolumeIndicators(cloneDeep(volumeIndicators));
    setSelectedEffectivenessValues(cloneDeep(effectivenessAndEfficiencyValues));
    setSelectedEfficiencyValues(cloneDeep(effectivenessAndEfficiencyValues));
  }

  return (
    <NetworkComparisonFiltersContext.Provider
      value={{
        displayedNetworkComparisonFilters,
        getNetworkComparisonFilterData,
        getNetworkComparisonFiltersPayload,
        mappedEffectivenessAndEfficiencyValues,
        mappedVolumeIndicators,
        onClearNetworkComparisonFiltersClick,
        originalDisplayedNetworkComparisonFilters,
        originalSelectedNetwork,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedNetwork,
        selectedPcaLineOfBusiness,
        selectedSpecialty,
        selectedVolumeIndicators,
        setDisplayedNetworkComparisonFilters,
        setOriginalDisplayedNetworkComparisonFilters,
        setSelectedEffectivenessValues,
        setSelectedEfficiencyValues,
        setSelectedNetwork,
        setSelectedPcaLineOfBusiness,
        setSelectedSpecialty,
        setSelectedVolumeIndicators,
      }}>
      {props.children}
    </NetworkComparisonFiltersContext.Provider>
  )
}

export default NetworkComparisonFiltersContextProvider;